/**
 * Pure helpers for the desktop single-screen estimate editor.
 * No new pricing math lives here — totals always come from helpers/estimateTotals.
 */
import { computeAreaSqft, resolveMeasurementDimensions } from "../../../../helpers/measurementModel";

export const EDITOR_URL_PARAMS = Object.freeze({ estimate: "est", feature: "feat" });

const LAST_FEATURE_KEY = "cp.estimateEditor.lastFeature";

/** Read the selected estimate/feature from a location.search string. */
export function readEditorUrl(search = "") {
  const params = new URLSearchParams(search);
  return {
    estimateId: params.get(EDITOR_URL_PARAMS.estimate) || "",
    featureId: params.get(EDITOR_URL_PARAMS.feature) || "",
  };
}

/** Return a new href with the editor params set (or removed when ids are empty). */
export function buildEditorHref(href, { estimateId, featureId }) {
  const url = new URL(href, "http://localhost");
  if (estimateId) url.searchParams.set(EDITOR_URL_PARAMS.estimate, estimateId);
  else url.searchParams.delete(EDITOR_URL_PARAMS.estimate);
  if (estimateId && featureId) url.searchParams.set(EDITOR_URL_PARAMS.feature, featureId);
  else url.searchParams.delete(EDITOR_URL_PARAMS.feature);
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * Keep the URL in step with the selection WITHOUT adding history entries:
 * the app owns a single Android/browser back-trap entry (useAppHardwareBack),
 * so we replaceState and preserve whatever state object is already there.
 */
export function syncEditorUrl(win, selection) {
  if (!win?.history?.replaceState) return;
  const next = buildEditorHref(win.location.href, selection);
  const current = `${win.location.pathname}${win.location.search}${win.location.hash}`;
  if (next !== current) win.history.replaceState(win.history.state, "", next);
}

export function rememberLastFeature(win, estimateId, featureId) {
  try {
    const store = JSON.parse(win.sessionStorage.getItem(LAST_FEATURE_KEY) || "{}");
    store[estimateId] = featureId;
    win.sessionStorage.setItem(LAST_FEATURE_KEY, JSON.stringify(store));
  } catch {
    /* storage unavailable — selection just won't be remembered */
  }
}

export function recallLastFeature(win, estimateId) {
  try {
    return JSON.parse(win.sessionStorage.getItem(LAST_FEATURE_KEY) || "{}")[estimateId] || "";
  } catch {
    return "";
  }
}

/** Locations as shown in the job tree (empty job-wide scope hidden, as on the phone workspace). */
export function visibleLocations(estimateTree) {
  return (estimateTree?.locations || []).filter(
    (location) => !location.is_default_scope || (location.workTypes || []).length > 0,
  );
}

/** Flat, ordered list of features across the tree for Previous / Next. */
export function flattenFeatures(estimateTree) {
  const out = [];
  visibleLocations(estimateTree).forEach((location) => {
    (location.workTypes || []).forEach((workType) => {
      out.push({ roomId: location.room_id, featureId: workType.estimate_feature_id });
    });
  });
  return out;
}

export function findFeature(estimateTree, featureId) {
  for (const location of estimateTree?.locations || []) {
    const workType = (location.workTypes || []).find((wt) => wt.estimate_feature_id === featureId);
    if (workType) return { location, workType };
  }
  return null;
}

/** Turn an in-progress measurement form into the row shape area math reads. Invalid drafts keep the saved row. */
export function overlaySectionDraft(section, draft) {
  if (!draft) return section;
  try {
    const dims = resolveMeasurementDimensions(draft);
    return {
      ...section,
      name: draft.name || section.name,
      record_kind: draft.recordKind || section.record_kind,
      surface_type: draft.surfaceType || section.surface_type,
      applies_to_surface_type: draft.recordKind === "deduction" ? (draft.appliesToSurfaceType || null) : null,
      applies_to_section_id: draft.recordKind === "deduction" ? (draft.appliesToSectionId || null) : null,
      length_in: dims.lengthIn,
      width_in: dims.widthIn,
      dimension_unit: dims.dimensionUnit,
      area_unit: dims.areaUnit,
      area_sqft: computeAreaSqft(dims.lengthIn, dims.widthIn),
    };
  } catch {
    return section;
  }
}

/**
 * Overlay in-progress (unsaved) cell values onto the loaded tree so the live
 * summary can be computed by the EXISTING totals helpers while the user types.
 * drafts: { materials, labour, sections } keyed by row id.
 */
export function applyDrafts(estimateTree, drafts) {
  const materialDrafts = drafts?.materials || {};
  const labourDrafts = drafts?.labour || {};
  const sectionDrafts = drafts?.sections || {};
  const dirty = Object.keys(materialDrafts).length + Object.keys(labourDrafts).length + Object.keys(sectionDrafts).length;
  if (!estimateTree || !dirty) return estimateTree;
  return {
    ...estimateTree,
    locations: (estimateTree.locations || []).map((location) => ({
      ...location,
      workTypes: (location.workTypes || []).map((workType) => ({
        ...workType,
        sections: (workType.sections || []).map((section) =>
          sectionDrafts[section.section_id] ? overlaySectionDraft(section, sectionDrafts[section.section_id]) : section,
        ),
        materials: (workType.materials || []).map((row) =>
          materialDrafts[row.room_material_id] ? { ...row, ...materialDrafts[row.room_material_id] } : row,
        ),
        labour: (workType.labour || []).map((row) =>
          labourDrafts[row.labour_id] ? { ...row, ...labourDrafts[row.labour_id] } : row,
        ),
      })),
    })),
  };
}
